import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getDiaryEntry, getRelatedBooks, getCollagesForDiaryEntry } from '../../data';
import './DiaryEntryPage.css';
import Breadcrumbs from '../common/Breadcrumbs';
import BookShelf from '../common/BookShelf';
import NotFound from '../common/NotFound';
import { getImagePaths } from '../../utils/imageUtils';
import useDocumentMeta from '../../hooks/useDocumentMeta';

const DiaryEntryPage: React.FC = () => {
    const { id } = useParams();
    const [isVisible, setIsVisible] = useState(false);
    const entry = getDiaryEntry(id || '');

    useDocumentMeta({
        title: entry && `${entry.title} — ${entry.date}`,
        description: entry && `${entry.content.substring(0, 155)}…`,
        type: 'article'
    });

    useEffect(() => {
        setIsVisible(true);
    }, []);

    if (!entry) return <NotFound thing="diary entry" backTo={{ label: 'the diary', path: '/diary' }} />;

    const relatedBooks = getRelatedBooks(entry.bookIds);
    const relatedCollages = getCollagesForDiaryEntry(entry.id);

    return (
        <div className={`diary-entry-page ${isVisible ? 'visible' : ''}`}>
            <Breadcrumbs 
                items={[
                    { label: 'diary', path: '/diary' },
                    { label: entry.title }
                ]} 
            />
            
            <article className="diary-entry-main">
                <header className="diary-entry-header">
                    <div className="diary-entry-meta">
                        <time className="entry-date">{entry.date}</time>
                        {entry.location && (
                            <span className="entry-location">{entry.location}</span>
                        )}
                        {entry.mood && (
                            <span className="entry-mood">{entry.mood}</span>
                        )}
                    </div>
                    <h1>{entry.title}</h1>
                </header>
                
                <div className="diary-entry-content">
                    {entry.content}
                </div>
            </article>
            
            <div className="diary-entry-related">
                {relatedBooks.length > 0 && (
                    <section className="related-books">
                        <h2>Books Mentioned</h2>
                        {/* The accordion this replaced hid every cover behind a
                            click, to show the same description the book page
                            already carries. */}
                        <BookShelf books={relatedBooks} />
                    </section>
                )}
                
                {relatedCollages.length > 0 && (
                    <section className="related-collages">
                        <h2>Related Collages</h2>
                        <div className="collages-grid">
                            {relatedCollages.map(collage => (
                                <Link 
                                    to={`/collages/${collage.id}`} 
                                    key={collage.id}
                                    className="collage-card-mini"
                                >
                                    {/* Was `collage.images.main` — the full-size
                                        original, skipping both the WebP and the
                                        thumbnail. */}
                                    <img
                                        src={getImagePaths(collage.images.main).thumbnailWebp}
                                        alt={collage.title}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                    <div className="collage-info">
                                        <h3>{collage.title}</h3>
                                        <span className="collage-date">{collage.date}</span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
};

export default DiaryEntryPage; 